/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import type React from 'react';

import { type CommonProps } from '../_common/types';
import { ConditionalWrap } from '../conditional-wrap';
import { XMarkIconOutline } from '../icons/generated/heroIcons/XMarkIconOutline';

interface TagProps {
  /** The content of the tag */
  children: React.ReactNode;
  /** Type of the tag */
  type?: 'destructive' | 'default';
  /** Size of the tag */
  size?: 'small' | 'medium' | 'large';
  /** Callback function triggered when tag should be removed */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

/**
 * @deprecated Use `ReadOnlyTag`, `DismissibleTag`, or `SelectableTag` instead. This component will be removed in v5.
 *
 * Tag component is a small visual element that represents a tag.
 * A tag is always removable. If you want a non removable tag, use the StatusLabel component instead.
 */
export const Tag = ({
  children,
  type = 'default',
  size = 'medium',
  onClick,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', TagProps>) => {
  const classes = classNames(`ndl-tag`, className, {
    'ndl-destructive': type === 'destructive',
    'ndl-large': size === 'large',
    'ndl-medium': size === 'medium',
    'ndl-small': size === 'small',
  });

  return (
    <div
      className={classes}
      ref={ref}
      style={style}
      {...restProps}
      {...htmlAttributes}
    >
      <ConditionalWrap
        shouldWrap={type === 'destructive'}
        wrap={(children) => (
          <button type="button" onClick={onClick}>
            {children}
          </button>
        )}
      >
        <span className="ndl-tag-content">{children}</span>
      </ConditionalWrap>
      {type !== 'destructive' && (
        <button
          type="button"
          className="ndl-remove-icon"
          onClick={onClick}
          aria-label="Remove tag"
        >
          <XMarkIconOutline />
        </button>
      )}
    </div>
  );
};
